/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  Tooltip as RTooltip,
} from "recharts";
import Layout from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import TablePagination from "@/components/TablePagination";
import { apiClient } from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";
import {
  Smartphone,
  Globe,
  Users,
  Search,
  ArrowLeft,
  Loader2,
  Activity,
} from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * User row as returned by GET /admin/users (with the platform-aware
 * `expoPushToken` column exposed by admin.service.getAllUsers).
 */
interface UserRow {
  id: number;
  name: string;
  email: string;
  username: string | null;
  authProvider: string;
  avatar: string | null;
  createdAt: string;
  expoPushToken: string | null;
  _count?: { teams: number };
}

interface UsersResponse {
  users: UserRow[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

interface DashboardStatsPartial {
  totalUsers: number;
  platform?: {
    totalApp: number;
    totalWeb: number;
    activeApp: number;
    activeWeb: number;
    activeTotal: number;
  };
}

const PLATFORM_COLORS = {
  app: "#10b981", // emerald — represents the mobile app
  web: "#3b82f6", // blue — represents the browser
};

export default function AnalyticsUsersPage() {
  const [platformFilter, setPlatformFilter] = useState<"all" | "app" | "web">(
    "all",
  );
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);

  // Dashboard stats give us the aggregate platform breakdown for the top-
  // of-page KPI + donut. Same endpoint the main dashboard uses.
  const { data: stats } = useQuery<DashboardStatsPartial>({
    queryKey: ["analytics-users", "stats"],
    queryFn: () => apiClient.get(API_ENDPOINTS.DASHBOARD.STATS),
    staleTime: 30_000,
  });

  const { data: usersData, isLoading } = useQuery<UsersResponse>({
    queryKey: ["analytics-users", "list", platformFilter, search, page, limit],
    queryFn: () => {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (search) params.set("search", search);
      if (platformFilter !== "all") params.set("platform", platformFilter);
      return apiClient.get(`${API_ENDPOINTS.USERS.LIST}?${params.toString()}`);
    },
    staleTime: 15_000,
  });

  const users = usersData?.users ?? [];
  const pagination = usersData?.pagination;
  const platform = stats?.platform;
  const totalUsers = stats?.totalUsers ?? 0;

  const donutData = platform
    ? [
        { name: "App", value: platform.totalApp, color: PLATFORM_COLORS.app },
        { name: "Web", value: platform.totalWeb, color: PLATFORM_COLORS.web },
      ]
    : [];

  return (
    <Layout>
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-800 mb-2"
        >
          <ArrowLeft className="h-3 w-3" /> Back to Dashboard
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
              <Activity className="h-7 w-7 text-rose-600" />
              User Analytics · Platform Breakdown
            </h1>
            <p className="text-gray-500 mt-1 text-sm max-w-2xl">
              Where users are engaging with Ball Knowledge. <b>App</b> users have
              registered a mobile push token (installed the app). <b>Web</b>{" "}
              users have not. <b>Active</b> = distinct users with a team edit or
              daily challenge completion in the last 30 days.
            </p>
          </div>
        </div>
      </div>

      {/* Aggregate KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <MetricCard
          label="Total users"
          value={totalUsers.toLocaleString()}
          icon={<Users className="h-5 w-5" />}
          tone="slate"
        />
        <MetricCard
          label="Active (last 30d)"
          value={(platform?.activeTotal ?? 0).toLocaleString()}
          icon={<Activity className="h-5 w-5" />}
          tone="rose"
          sub={
            totalUsers > 0
              ? `${(((platform?.activeTotal ?? 0) / totalUsers) * 100).toFixed(1)}% engagement`
              : undefined
          }
        />
        <MetricCard
          label="On App"
          value={(platform?.totalApp ?? 0).toLocaleString()}
          icon={<Smartphone className="h-5 w-5" />}
          tone="emerald"
          sub={
            platform
              ? `${platform.activeApp.toLocaleString()} active`
              : undefined
          }
        />
        <MetricCard
          label="On Web"
          value={(platform?.totalWeb ?? 0).toLocaleString()}
          icon={<Globe className="h-5 w-5" />}
          tone="blue"
          sub={
            platform
              ? `${platform.activeWeb.toLocaleString()} active`
              : undefined
          }
        />
      </div>

      {/* Donut + Active-vs-Total bars */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Card className="border border-gray-200 shadow-sm">
          <CardContent className="p-5">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-4">
              Platform split (all users)
            </h3>
            {platform && donutData.reduce((s, d) => s + d.value, 0) > 0 ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={donutData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={95}
                      paddingAngle={3}
                    >
                      {donutData.map((d) => (
                        <Cell key={d.name} fill={d.color} />
                      ))}
                    </Pie>
                    <RTooltip
                      contentStyle={{
                        backgroundColor: "white",
                        border: "1px solid #e5e7eb",
                        borderRadius: 10,
                        fontSize: 12,
                      }}
                    />
                    <Legend
                      verticalAlign="bottom"
                      height={30}
                      formatter={(v) => (
                        <span className="text-xs font-semibold text-gray-700">
                          {v}
                        </span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-400 text-sm">
                No user data yet
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border border-gray-200 shadow-sm">
          <CardContent className="p-5">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-4">
              Active vs Total by Platform
            </h3>
            {platform ? (
              <div className="space-y-5">
                <PlatformBar
                  label="App"
                  color={PLATFORM_COLORS.app}
                  active={platform.activeApp}
                  total={platform.totalApp}
                />
                <PlatformBar
                  label="Web"
                  color={PLATFORM_COLORS.web}
                  active={platform.activeWeb}
                  total={platform.totalWeb}
                />
                <div className="pt-4 border-t text-xs text-gray-500 space-y-1">
                  <p>
                    <b>Active</b> = users with a team edit or daily challenge
                    completed in the last 30 days.
                  </p>
                  <p>
                    <b>Platform</b> = based on mobile push token registration.
                    Users may still hit the site from a browser occasionally.
                  </p>
                </div>
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-400 text-sm">
                Loading…
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Users list */}
      <Card className="border border-gray-200 shadow-sm">
        <CardContent className="p-5">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">
              Users
              {pagination
                ? ` · ${pagination.total.toLocaleString()} match${pagination.total === 1 ? "" : "es"}`
                : ""}
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <Input
                  placeholder="Search name, email, username…"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="pl-8 h-9 w-64"
                />
              </div>
              <Select
                value={platformFilter}
                onValueChange={(v: any) => {
                  setPlatformFilter(v);
                  setPage(1);
                }}
              >
                <SelectTrigger className="h-9 w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All platforms</SelectItem>
                  <SelectItem value="app">App only</SelectItem>
                  <SelectItem value="web">Web only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {isLoading ? (
            <div className="h-40 flex items-center justify-center text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
              Loading users…
            </div>
          ) : users.length === 0 ? (
            <div className="h-40 flex items-center justify-center text-gray-400 text-sm">
              No users match these filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs font-semibold text-gray-500 uppercase tracking-wide border-b">
                    <th className="text-left py-2 pl-2">User</th>
                    <th className="text-left py-2">Platform</th>
                    <th className="text-left py-2">Auth</th>
                    <th className="text-center py-2">Teams</th>
                    <th className="text-right py-2 pr-2">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => {
                    const isApp = !!u.expoPushToken;
                    return (
                      <tr
                        key={u.id}
                        className="border-b border-gray-50 hover:bg-gray-50/70"
                      >
                        <td className="py-2 pl-2">
                          <div className="flex items-center gap-2">
                            {u.avatar ? (
                              <img
                                src={u.avatar}
                                alt=""
                                className="w-7 h-7 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-[10px] font-bold">
                                {(u.name || "?").charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div className="min-w-0">
                              <div className="text-xs font-semibold text-gray-900 truncate">
                                {u.name}
                              </div>
                              <div className="text-[10px] text-gray-500 truncate">
                                {u.username ? `@${u.username}` : u.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="py-2">
                          <span
                            className={cn(
                              "inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full",
                              isApp
                                ? "bg-emerald-50 text-emerald-700"
                                : "bg-blue-50 text-blue-700",
                            )}
                          >
                            {isApp ? (
                              <>
                                <Smartphone className="h-3 w-3" /> APP
                              </>
                            ) : (
                              <>
                                <Globe className="h-3 w-3" /> WEB
                              </>
                            )}
                          </span>
                        </td>
                        <td className="py-2 text-xs text-gray-600 uppercase">
                          {u.authProvider}
                        </td>
                        <td className="py-2 text-center text-xs text-gray-700">
                          {u._count?.teams ?? 0}
                        </td>
                        <td className="py-2 pr-2 text-right text-[11px] text-gray-500">
                          {new Date(u.createdAt).toLocaleDateString(undefined, {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {pagination && pagination.total > pagination.limit && (
            <div className="mt-4">
              <TablePagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                totalItems={pagination.total}
                pageSize={pagination.limit}
                onPageChange={setPage}
                onPageSizeChange={(l) => {
                  setLimit(l);
                  setPage(1);
                }}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </Layout>
  );
}

// ── Subcomponents ──────────────────────────────────────────────────────────

function MetricCard({
  label,
  value,
  icon,
  tone,
  sub,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  tone: "slate" | "rose" | "emerald" | "blue";
  sub?: string;
}) {
  const toneMap: Record<string, string> = {
    slate: "bg-slate-50 text-slate-600",
    rose: "bg-rose-50 text-rose-600",
    emerald: "bg-emerald-50 text-emerald-600",
    blue: "bg-blue-50 text-blue-600",
  };
  return (
    <Card className="border border-gray-200 shadow-sm">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">
            {label}
          </p>
          <div
            className={cn(
              "w-8 h-8 rounded-lg flex items-center justify-center",
              toneMap[tone],
            )}
          >
            {icon}
          </div>
        </div>
        <p className="text-2xl font-extrabold text-gray-900 leading-tight">
          {value}
        </p>
        {sub && <p className="text-[11px] text-gray-500 mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

function PlatformBar({
  label,
  color,
  active,
  total,
}: {
  label: string;
  color: string;
  active: number;
  total: number;
}) {
  const pct = total > 0 ? Math.round((active / total) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-bold text-gray-700 flex items-center gap-1.5">
          <span
            className="inline-block w-2.5 h-2.5 rounded-sm"
            style={{ backgroundColor: color }}
          />
          {label}
        </span>
        <span className="text-xs text-gray-500">
          <span className="font-bold text-gray-900">
            {active.toLocaleString()}
          </span>{" "}
          / {total.toLocaleString()} active ({pct}%)
        </span>
      </div>
      <div className="h-3 w-full bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}
